import { headPosition } from "@/lib/fractional";

// Mirrors app/lib/lex.rb. A key is only a prediction: where the server would respread
// or raise, the client still answers with a key that sorts in the intended place.
const MIN_CHAR = "a";
const MAX_CHAR = "z";
const MID = "n";
const BASE = 26;
const HEAD_ROOM = 16;

const shifted = (char: string, by: number) => String.fromCharCode(char.charCodeAt(0) + by);

function increment(key: string): string {
  return key + MID;
}

function decrement(key: string): string {
  const last = key[key.length - 1];
  if (last !== MIN_CHAR) return key.slice(0, -1) + shifted(last, -1);
  if ([...key].every((char) => char === MIN_CHAR)) return key.slice(0, -1);
  return decrement(key.slice(0, -1)) + MAX_CHAR;
}

function midpoint(left: string, right: string): string {
  let common = 0;
  while (common < Math.min(left.length, right.length) && left[common] === right[common]) common += 1;
  const prefix = left.slice(0, common);
  const restLeft = left.slice(common);
  const restRight = right.slice(common);
  if (restLeft === "") return prefix + decrement(restRight);

  const leftCode = restLeft.charCodeAt(0);
  const rightCode = restRight.charCodeAt(0);
  if (rightCode - leftCode > 1) return prefix + String.fromCharCode(Math.floor((leftCode + rightCode) / 2));
  return prefix + restLeft[0] + increment(restLeft.slice(1));
}

export function keyBetween(prev: string | null, succ: string | null): string {
  if (prev === null && succ === null) return MID;
  if (prev === null) return succ === MIN_CHAR ? headPosition(succ) : decrement(succ!);
  if (succ === null || prev >= succ) return increment(prev);
  return midpoint(prev, succ);
}

export const headKey = (first: string | null) => keyBetween(null, first);
export const tailKey = (last: string | null) => keyBetween(last, null);

function encode(value: number, width: number): string {
  let key = "";
  for (let place = width; place >= 1; place -= 1) {
    key += shifted(MIN_CHAR, Math.floor(value / BASE ** (place - 1)) % BASE);
  }
  return key;
}

export function spreadKeys(count: number): string[] {
  if (count <= 0) return [];
  let width = 1;
  while (BASE ** width < (count + 1) * HEAD_ROOM) width += 1;
  const step = BASE ** width / (count + 1);
  return Array.from({ length: count }, (_, i) => encode(Math.floor((i + 1) * step), width));
}
